import {
  BufferTarget,
  MediaStreamAudioTrackSource,
  MediaStreamVideoTrackSource,
  Mp4OutputFormat,
  Output,
} from 'mediabunny';

export function isMp4MediabunnyRecordingSupported() {
  return typeof VideoEncoder !== 'undefined'
    && typeof AudioEncoder !== 'undefined';
}

export function createMp4StreamRecorder({
  videoBitsPerSecond = 750_000,
  audioBitsPerSecond = 48_000,
  frameRate = 30,
} = {}) {
  let output = null;
  let videoSource = null;
  let audioSource = null;
  let captureStream = null;

  const failIfSourceError = (error) => {
    throw error instanceof Error ? error : new Error('خطا در ضبط MP4');
  };

  return {
    async start(stream) {
      if (!stream) {
        throw new Error('استریم ویدیو در دسترس نیست.');
      }

      const videoTrack = stream.getVideoTracks()[0];
      if (!videoTrack) {
        throw new Error('ترک ویدیو برای ضبط یافت نشد.');
      }

      captureStream = stream;
      output = new Output({
        format: new Mp4OutputFormat(),
        target: new BufferTarget(),
      });

      // یک frame rate صریح می‌دهیم تا در مرورگرهایی که MediaStreamTrackProcessor
      // ندارند (مثل Firefox) mediabunny از مسیر جایگزین استفاده کند و به‌جای پرتاب
      // خطای «MediaStreamTrackProcessor is required» فریم‌ها را با تایمر بخواند.
      const trackFrameRate = Number(videoTrack.getSettings?.().frameRate) || frameRate;

      videoSource = new MediaStreamVideoTrackSource(videoTrack, {
        codec: 'avc',
        bitrate: videoBitsPerSecond,
        frameRate: trackFrameRate,
      });
      output.addVideoTrack(videoSource);

      const audioTrack = stream.getAudioTracks()[0];
      if (audioTrack) {
        audioSource = new MediaStreamAudioTrackSource(audioTrack, {
          codec: 'aac',
          bitrate: audioBitsPerSecond,
        });
        output.addAudioTrack(audioSource);
      }

      videoSource.errorPromise.catch(failIfSourceError);
      audioSource?.errorPromise.catch(failIfSourceError);

      await output.start();
    },

    async stop() {
      if (!output) {
        throw new Error('ضبط فعال نیست.');
      }

      const activeOutput = output;
      output = null;
      videoSource = null;
      audioSource = null;
      captureStream = null;

      await activeOutput.finalize();

      const buffer = activeOutput.target?.buffer;
      if (!buffer || buffer.byteLength === 0) {
        throw new Error('ضبط خالی بود.');
      }

      return {
        blob: new Blob([buffer], { type: 'video/mp4' }),
        mimeType: 'video/mp4',
      };
    },

    async cancel() {
      if (!output) {
        return;
      }

      const activeOutput = output;
      output = null;
      videoSource = null;
      audioSource = null;
      captureStream = null;

      try {
        await activeOutput.cancel();
      } catch (error) {
        // ignore cancel errors during teardown
      }
    },
  };
}
